import React, { useState } from "react";
import { motion } from "framer-motion";
import Footer from "../Footer/Footer";
import { Modal, Button } from "react-bootstrap";
import { FaPlay, FaClock, FaUser, FaTimes } from "react-icons/fa";

const Tutorials = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Tutorial videos data with actual videos
  const tutorials = [
    {
      id: 1,
      title: "How to Upload a New Schema",
      description: "Learn how to upload and configure new schemas in the Nutrad platform for your product data management needs.",
      thumbnail: "/thumbnails/schema-upload-thumb.svg",
      videoUrl: "/videos/HowToUploadANewSchema.mp4",
      duration: "5:30",
      instructor: "Nutrad Team",
      level: "Beginner",
      topics: ["Schema Upload", "Configuration", "Data Structure", "Setup"]
    },
    {
      id: 2,
      title: "How to Create a New Static Field",
      description: "Step-by-step guide on creating static fields to enhance your product data with custom attributes and properties.",
      thumbnail: "/thumbnails/static-field-thumb.svg",
      videoUrl: "/videos/HowtoCreateANewStaticField.mp4",
      duration: "4:15",
      instructor: "Nutrad Team",
      level: "Beginner",
      topics: ["Static Fields", "Custom Attributes", "Field Creation", "Data Management"]
    }
  ];

  const handleVideoClick = (tutorial) => {
    setSelectedVideo(tutorial);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedVideo(null);
  };

  const getLevelColor = (level) => {
    switch (level) {
      case "Beginner":
        return "bg-green-100 text-green-800";
      case "Intermediate":
        return "bg-yellow-100 text-yellow-800";
      case "Advanced":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Video Tutorials
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Learn how to make the most of the Nutrad platform with our comprehensive video tutorials. 
              From basic navigation to advanced workflows, we've got you covered.
            </p>
          </motion.div>

          {/* Video Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tutorials.map((tutorial, index) => (
              <motion.div
                key={tutorial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                onClick={() => handleVideoClick(tutorial)}
              >
                {/* Thumbnail */}
                <div className="relative">
                  <img
                    src={tutorial.thumbnail}
                    alt={tutorial.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                    <FaPlay className="text-white text-4xl" />
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm flex items-center">
                    <FaClock className="mr-1" />
                    {tutorial.duration}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(tutorial.level)}`}>
                      {tutorial.level}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {tutorial.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {tutorial.description}
                  </p>

                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <FaUser className="mr-1" />
                    {tutorial.instructor}
                  </div>

                  {/* Topics */}
                  <div className="flex flex-wrap gap-1">
                    {tutorial.topics.slice(0, 3).map((topic, topicIndex) => (
                      <span
                        key={topicIndex}
                        className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                      >
                        {topic}
                      </span>
                    ))}
                    {tutorial.topics.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{tutorial.topics.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Additional Resources */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-16 text-center"
          >
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Need More Help?
              </h2>
              <p className="text-gray-600 mb-6">
                Can't find what you're looking for? Our support team is here to help you succeed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="primary"
                  size="lg"
                  href="/support"
                  className="bg-blue-600 hover:bg-blue-700 border-blue-600"
                >
                  Contact Support
                </Button>
                <Button
                  variant="outline-primary"
                  size="lg"
                  href="mailto:support@nutrad.io"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  Email Us
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="xl"
        centered
        className="video-modal"
      >
        <Modal.Header className="border-0 pb-0">
          <Modal.Title className="text-xl font-semibold">
            {selectedVideo?.title}
          </Modal.Title>
          <Button
            variant="link"
            onClick={handleCloseModal}
            className="text-gray-500 hover:text-gray-700 p-0 border-0"
          >
            <FaTimes size={20} />
          </Button>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedVideo && (
            <div className="aspect-w-16 aspect-h-9">
              {selectedVideo.videoUrl.endsWith('.mp4') ? (
                <video
                  className="w-full h-96"
                  controls
                  preload="metadata"
                  onError={(e) => console.error('Video loading error:', e)}
                  onLoadStart={() => console.log('Video loading started')}
                >
                  <source src={selectedVideo.videoUrl} type="video/mp4" />
                  <p className="p-4 text-center text-gray-600">
                    Your browser does not support the video tag.
                    <a href={selectedVideo.videoUrl} className="text-blue-600 hover:underline ml-1">
                      Download the video instead
                    </a>
                  </p>
                </video>
              ) : (
                <iframe
                  src={selectedVideo.videoUrl}
                  title={selectedVideo.title}
                  className="w-full h-96"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              )}
            </div>
          )}
          <div className="p-6">
            <p className="text-gray-600 mb-4">{selectedVideo?.description}</p>
            <div className="flex flex-wrap gap-2">
              {selectedVideo?.topics.map((topic, index) => (
                <span
                  key={index}
                  className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Footer />
    </>
  );
};

export default Tutorials;
